import { GraphicStyleTextAsset } from '../text/GraphicStyleTextAsset';
import { TimeInvariantBase } from '../TimeInvariantBase';
import type { DestroyOptions } from '../../types';
import type { BaseSettings } from '../TimeAwareBase';
import type { CaptionStyle } from './CaptionsManager';


/**
 * Implementation of `CaptionStyle`.
 *
 * The style wraps a GraphicStyleTextAsset that is used to manage the styling. An alternative would be to manage the
 * state here, but there are many assertion and checks that are already done by the text asset which are better reused.
 */
export class CaptionAssetStyle extends TimeInvariantBase implements CaptionStyle {
    override _classType = 'CaptionAssetStyle';

    #textAsset: GraphicStyleTextAsset;

    protected _requiresRedraw = false;

    constructor(settings: BaseSettings) {
        super(settings);

        const container = {
            root: this.root,
            slide: {},
        };

        this.#textAsset = new GraphicStyleTextAsset(container, {
            isFinalTopLevel: true,
            autoDraw: true,
            fetchResources: false,
        });

        // When the normal or highlight styles are updated, then this CaptionAssetStyle has also been updated.
        this.#textAsset.on('childUpdated', () => {
            this.requiresUpdate = true;
        });
    }

    get transformation() {
        return this.#textAsset.transformation;
    }

    get maxWidth(): number {
        return this.#textAsset.maxWidth;
    }

    set maxWidth(value: number) {
        this.#textAsset.maxWidth = value;
    }

    get maxHeight(): number {
        return this.#textAsset.maxHeight;
    }

    set maxHeight(value: number) {
        this.#textAsset.maxHeight = value;
    }

    get textAlign(): string {
        return this.#textAsset.textAlign;
    }

    set textAlign(value: string) {
        this.#textAsset.textAlign = value;
    }

    get textScaleFactor(): number {
        return this.#textAsset.textScaleFactor;
    }

    set textScaleFactor(value: number) {
        this.#textAsset.textScaleFactor = value;
    }

    get leadingMultiplier(): number {
        return this.#textAsset.leadingMultiplier;
    }

    set leadingMultiplier(value: number) {
        this.#textAsset.leadingMultiplier = value;
    }

    get highlightedTextScale(): number {
        return this.#textAsset.highlightedTextScale;
    }

    set highlightedTextScale(value: number) {
        this.#textAsset.highlightedTextScale = value;
    }

    get visible(): boolean {
        return this.#textAsset.visible;
    }

    set visible(value: boolean) {
        this.#textAsset.visible = value;
    }

    get textSize(): number {
        return this.#textAsset.textSize;
    }

    set textSize(value: number) {
        this.#textAsset.textSize = value;
    }

    get readingDirection(): string {
        return this.#textAsset.readingDirection;
    }

    set readingDirection(value: string) {
        this.#textAsset.readingDirection = value;
    }

    get normalStyle() {
        return this.#textAsset.normalStyle;
    }

    get highlightStyle() {
        return this.#textAsset.highlightStyle;
    }

    override get editableProperties(): string[] {
        return [];
    }

    override get readonlyProperties(): string[] {
        return [];
    }

    override get propertiesToSerialize(): string[] {
        return [
            'transformation',
            'maxWidth',
            'maxHeight',
            'textAlign',
            'textScaleFactor',
            'leadingMultiplier',
            'highlightedTextScale',
            'visible',
            'textSize',
            'readingDirection',
            'normalStyle',
            'highlightStyle',
        ];
    }

    override destroy(args?: DestroyOptions) {
        super.destroy(args);
        this.#textAsset.destroy(args);
    }
}
